import { useEffect, useMemo, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { SortableColumnPicker } from "@/components/dataTable";
import { defaultSelectedQuestionKeys, type EvaluationQuestion } from "./evaluationModel";

export interface QuestionSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  questions: EvaluationQuestion[];
  selected: string[];
  onSave: (next: string[]) => Promise<void> | void;
}

type RoleFilter = "all" | "defect" | "material" | "measurement" | "other";

/**
 * Auswahl und Reihenfolge der Fragen, nach denen die Wartung ausgewertet wird.
 * Wird pro Benutzer und Wartungsart gespeichert.
 */
export function QuestionSettingsDialog({ open, onClose, questions, selected, onSave }: QuestionSettingsDialogProps) {
  const available = useMemo(() => questions.map((q) => q.key), [questions]);
  const defaults = useMemo(() => defaultSelectedQuestionKeys(questions), [questions]);
  const labelByKey = useMemo(() => new Map(questions.map((q) => [q.key, q])), [questions]);

  const [current, setCurrent] = useState<string[]>(selected);
  const [baseline, setBaseline] = useState<string[]>(selected);
  const [saving, setSaving] = useState(false);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  useEffect(() => {
    if (!open) return;
    setCurrent(selected);
    setBaseline(selected);
    setRoleFilter("all");
  }, [open, selected]);

  const dirty = useMemo(() => hasFormChanges(current, baseline), [current, baseline]);
  const requestClose = useDialogRequestClose(onClose);

  function selectByRole(role: RoleFilter) {
    const keys = questions
      .filter((q) => {
        if (role === "all") return true;
        if (role === "defect") return q.isDefect;
        if (role === "material") return q.isMaterial;
        if (role === "measurement") return q.role === "measurement";
        return !q.isDefect && !q.isMaterial && q.role !== "measurement";
      })
      .map((q) => q.key);
    setCurrent(Array.from(new Set([...current, ...keys])));
  }

  function getLabel(key: string): string {
    const q = labelByKey.get(key);
    if (!q) return key;
    return q.roleLabel ? `${q.label} · ${q.roleLabel}` : q.label;
  }

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(current);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const counts = useMemo(
    () => ({
      defect: questions.filter((q) => q.isDefect).length,
      material: questions.filter((q) => q.isMaterial).length,
      measurement: questions.filter((q) => q.role === "measurement").length,
    }),
    [questions],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Fragen für die Auswertung"
      description="Wähle, welche Fragen des Wartungsformulars ausgewertet werden, und lege ihre Reihenfolge fest. Mängel und Material werden nur aus den ausgewählten Fragen ermittelt."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => setCurrent(defaults)} disabled={saving}>
            Alle Fragen
          </Button>
          <Button variant="ghost" onClick={() => setCurrent([])} disabled={saving}>
            Keine
          </Button>
          <Button variant="ghost" onClick={requestClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} loading={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-label-secondary">Schnellauswahl:</span>
        <button
          type="button"
          className="rounded-full bg-fill-tertiary px-2.5 py-1 font-medium text-label hover:bg-fill-secondary disabled:opacity-50"
          disabled={!counts.defect || saving}
          onClick={() => {
            setRoleFilter("defect");
            selectByRole("defect");
          }}
        >
          Mangel-Fragen ({counts.defect})
        </button>
        <button
          type="button"
          className="rounded-full bg-fill-tertiary px-2.5 py-1 font-medium text-label hover:bg-fill-secondary disabled:opacity-50"
          disabled={!counts.material || saving}
          onClick={() => {
            setRoleFilter("material");
            selectByRole("material");
          }}
        >
          Material-Fragen ({counts.material})
        </button>
        <button
          type="button"
          className="rounded-full bg-fill-tertiary px-2.5 py-1 font-medium text-label hover:bg-fill-secondary disabled:opacity-50"
          disabled={!counts.measurement || saving}
          onClick={() => {
            setRoleFilter("measurement");
            selectByRole("measurement");
          }}
        >
          Messwerte ({counts.measurement})
        </button>
        {roleFilter !== "all" ? (
          <span className="text-label-tertiary">Fragen dieser Rolle wurden zur Auswahl hinzugefügt.</span>
        ) : null}
      </div>

      {questions.length === 0 ? (
        <p className="text-sm text-label-secondary">
          Das Wartungsformular enthält keine auswertbaren Inspektionsfragen.
        </p>
      ) : (
        <SortableColumnPicker
          available={available}
          selected={current}
          getLabel={getLabel}
          onSelectedChange={setCurrent}
          disabled={saving}
        />
      )}
    </Dialog>
  );
}
